import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { useThemeStore } from '@/stores/theme';
import { useBrandingStore } from '@/stores/branding';
import { useBrandingLogoSrc } from '@/hooks/useBrandingLogoSrc';
import { useAuthStore } from '@/stores/auth';
import {
  LayoutDashboard, CalendarDays, KanbanSquare, Megaphone,
  BarChart3, Users, Building2, Settings, HelpCircle,
  ChevronLeft, ChevronRight, Target, UserCircle, Layers,
  Bot, Link2, Bell, FileText, Crosshair, ScrollText,
  PenSquare, Share2, Workflow, Images, Zap, CalendarRange, X,
} from 'lucide-react';
import { isSupplierPortalUser } from '@/lib/supplier-portal-user';

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path: string;
  resource?: string;
}

interface NavSection {
  id: string;
  label: string | null;
  items: NavItem[];
}

/** Reduced navigation for pure supplier accounts (no internal org roles). */
const supplierPortalNavSections: NavSection[] = [
  {
    id: 'supplier',
    label: null,
    items: [
      { label: 'Zulieferer-Portal', icon: <Building2 size={20} />, path: '/supplier-portal' },
      { label: 'Posteingang', icon: <Bell size={20} />, path: '/notifications' },
      { label: 'Aktivitäten', icon: <KanbanSquare size={20} />, path: '/activities', resource: 'marketing_activities' },
      { label: 'Kampagnen', icon: <Megaphone size={20} />, path: '/campaigns', resource: 'campaigns' },
      { label: 'Dokumente', icon: <FileText size={20} />, path: '/documents', resource: 'documents' },
    ],
  },
];

const navSections: NavSection[] = [
  {
    id: 'today',
    label: null,
    items: [
      { label: 'Heute', icon: <LayoutDashboard size={20} />, path: '/dashboard' },
      { label: 'Posteingang', icon: <Bell size={20} />, path: '/notifications' },
    ],
  },
  {
    id: 'content',
    label: 'Content',
    items: [
      { label: 'Content-Planner', icon: <CalendarDays size={20} />, path: '/planner', resource: 'social_posts' },
      { label: 'Composer', icon: <PenSquare size={20} />, path: '/composer', resource: 'social_posts' },
      { label: 'Social Hub', icon: <Share2 size={20} />, path: '/social', resource: 'social_accounts' },
      { label: 'Medien', icon: <Images size={20} />, path: '/media', resource: 'media_library' },
    ],
  },
  {
    id: 'plan',
    label: 'Planen',
    items: [
      { label: 'Strategie', icon: <Crosshair size={20} />, path: '/marketing-strategy', resource: 'marketing_plans' },
      { label: 'Marketingplan', icon: <Target size={20} />, path: '/marketing-plan', resource: 'marketing_plans' },
      { label: 'Kampagnen', icon: <Megaphone size={20} />, path: '/campaigns', resource: 'campaigns' },
      { label: 'Aktivitäten', icon: <KanbanSquare size={20} />, path: '/activities', resource: 'marketing_activities' },
      { label: 'Gesamtkalender', icon: <CalendarRange size={20} />, path: '/calendar', resource: 'marketing_activities' },
    ],
  },
  {
    id: 'analyze',
    label: 'Auswerten',
    items: [
      { label: 'Insights', icon: <BarChart3 size={20} />, path: '/statistics', resource: 'statistics' },
      { label: 'Automation', icon: <Workflow size={20} />, path: '/automation', resource: 'campaigns' },
    ],
  },
  {
    id: 'more',
    label: 'Mehr',
    items: [
      { label: 'Companion', icon: <Bot size={20} />, path: '/companion', resource: 'ai_agents' },
      { label: 'Teams', icon: <Users size={20} />, path: '/teams', resource: 'teams' },
      { label: 'Zulieferer', icon: <Building2 size={20} />, path: '/suppliers', resource: 'suppliers' },
      { label: 'Dokumente', icon: <FileText size={20} />, path: '/documents', resource: 'documents' },
    ],
  },
];

const adminItems: NavItem[] = [
  { label: 'Benutzer', icon: <UserCircle size={20} />, path: '/admin/users', resource: 'users' },
  { label: 'Rollen', icon: <Layers size={20} />, path: '/admin/roles', resource: 'roles' },
  { label: 'Integrationen', icon: <Link2 size={20} />, path: '/admin/integrations', resource: 'integrations' },
  { label: 'Benachrichtigungen', icon: <Bell size={20} />, path: '/admin/notifications', resource: 'notifications' },
  { label: 'Audit-Log', icon: <ScrollText size={20} />, path: '/admin/audit', resource: 'audit_log' },
  { label: 'Einstellungen', icon: <Settings size={20} />, path: '/admin/settings', resource: 'settings' },
];

interface SidebarProps {
  currentPath: string;
  onNavigate: (path: string) => void;
  mobile?: boolean;
  onClose?: () => void;
}

export function Sidebar({ currentPath, onNavigate, mobile = false, onClose }: SidebarProps) {
  const { sidebarCollapsed: desktopCollapsed, toggleSidebar } = useThemeStore();
  const sidebarCollapsed = !mobile && desktopCollapsed;
  const { user } = useAuthStore();
  const appName = useBrandingStore((s) => s.app_name);
  const logoSrc = useBrandingLogoSrc();
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => {
    setLogoFailed(false);
  }, [logoSrc]);
  const roles = ((user as { roles?: string[] } | null)?.roles) ?? [];
  const isAdmin = roles.includes('super_admin') || roles.includes('admin');
  const supplierPortal = isSupplierPortalUser(roles);
  const sections = supplierPortal ? supplierPortalNavSections : navSections;

  const isNavActive = (path: string) =>
    currentPath === path ||
    currentPath.startsWith(`${path}/`) ||
    (path === '/social' && currentPath.startsWith('/linkedin')) ||
    (path === '/calendar' && (currentPath === '/marketing-calendar' || currentPath === '/activities/calendar')) ||
    (supplierPortal && path === '/supplier-portal' && currentPath === '/dashboard');

  return (
    <aside
      className={cn(
        'border-r border-border bg-card transition-[width] duration-200',
        mobile ? 'h-dvh w-full' : 'fixed left-0 top-0 z-40 h-screen',
        !mobile && (sidebarCollapsed ? 'w-[68px]' : 'w-[260px]')
      )}
    >
      <div className="flex h-16 items-center justify-between border-b border-border px-3">
        {!sidebarCollapsed && (
          <div className="flex min-w-0 items-center gap-2.5 px-1">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md bg-card-hover">
              {logoFailed ? (
                <div className="flex h-full w-full items-center justify-center rounded-md bg-primary text-[10px] font-bold text-primary-foreground">
                  LC
                </div>
              ) : (
                <img
                  src={logoSrc}
                  alt=""
                  className="max-h-8 max-w-8 object-contain p-0.5"
                  onError={() => setLogoFailed(true)}
                />
              )}
            </div>
            <span className="truncate text-sm font-semibold text-foreground">{appName}</span>
          </div>
        )}
        {sidebarCollapsed && (
          <div className="mx-auto flex h-8 w-8 items-center justify-center overflow-hidden rounded-md bg-card-hover">
            {logoFailed ? (
              <div className="flex h-full w-full items-center justify-center rounded-md bg-primary text-[10px] font-bold text-primary-foreground">
                LC
              </div>
            ) : (
              <img
                src={logoSrc}
                alt=""
                className="max-h-8 max-w-8 object-contain p-0.5"
                onError={() => setLogoFailed(true)}
              />
            )}
          </div>
        )}
        <button
          type="button"
          onClick={mobile ? onClose : toggleSidebar}
          className={cn(
            'rounded-md p-1.5 text-muted hover:bg-card-hover hover:text-foreground dark:hover:text-foreground',
            sidebarCollapsed && 'absolute -right-3 top-5 z-50 rounded-full border border-border bg-card'
          )}
          aria-label={mobile ? 'Navigation schließen' : sidebarCollapsed ? 'Sidebar erweitern' : 'Sidebar einklappen'}
        >
          {mobile ? <X size={18} /> : sidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      <nav aria-label="Hauptnavigation" className="flex h-[calc(100dvh-64px)] flex-col gap-1 overflow-y-auto p-2">
        {sections.map((section) => (
          <div key={section.id} className={cn('space-y-0.5', section.label && 'mt-3 first:mt-0')}>
            {section.label && !sidebarCollapsed && (
              <span className="mb-1 block px-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted">
                {section.label}
              </span>
            )}
            {section.label && sidebarCollapsed && (
              <div className="mx-auto my-2 h-px w-6 bg-card-hover" aria-hidden />
            )}
            {section.items.map((item) => (
              <SidebarItem
                key={item.path}
                item={item}
                isActive={isNavActive(item.path)}
                collapsed={sidebarCollapsed}
                onNavigate={onNavigate}
                emphasize={item.path === '/planner'}
              />
            ))}
          </div>
        ))}

        {isAdmin && (
          <>
            <div className="my-3 border-t border-border" />
            {!sidebarCollapsed && (
              <span className="mb-1 px-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted">
                Administration
              </span>
            )}
            <div className="space-y-0.5">
              {adminItems.map((item) => (
                <SidebarItem
                  key={item.path}
                  item={item}
                  isActive={currentPath === item.path || currentPath.startsWith(item.path + '/')}
                  collapsed={sidebarCollapsed}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          </>
        )}

        <div className="mt-auto border-t border-border pt-2">
          <SidebarItem
            item={{ label: 'Hilfe', icon: <HelpCircle size={20} />, path: '/help' }}
            isActive={currentPath === '/help'}
            collapsed={sidebarCollapsed}
            onNavigate={onNavigate}
          />
        </div>
      </nav>
    </aside>
  );
}

function SidebarItem({
  item, isActive, collapsed, onNavigate, emphasize,
}: {
  item: NavItem;
  isActive: boolean;
  collapsed: boolean;
  onNavigate: (path: string) => void;
  emphasize?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => onNavigate(item.path)}
      className={cn(
        'flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
        isActive
          ? 'bg-primary text-primary-foreground'
          : emphasize
            ? 'text-foreground hover:bg-card-hover'
            : 'text-muted hover:bg-card-hover hover:text-foreground',
        collapsed && 'justify-center px-2'
      )}
      title={collapsed ? item.label : undefined}
      aria-label={item.label}
      aria-current={isActive ? 'page' : undefined}
    >
      <span className="shrink-0">{item.icon}</span>
      {!collapsed && (
        <span className="flex min-w-0 flex-1 items-center gap-2 truncate">
          {item.label}
          {emphasize && !isActive && (
            <Zap size={12} className="shrink-0 text-muted" aria-hidden />
          )}
        </span>
      )}
    </button>
  );
}
